import { z } from 'zod';
import { apiEnvelope } from '../envelope';
import { ProgramConfig, ProgramConfigRead, programDomainSchema } from '../programs/schema';

// Wire schema for `GET /api/v1/members/me/substance-config`. Returns the
// member's `program_definitions.config` verbatim per `member_program`, ordered
// main-program-first — the condition-agnostic seam that replaces the prototype's
// hardcoded `substanceConfig.ts`. Resolving `config.checkin_schema` to a
// renderable form is the client's (C-checkin), not this endpoint's.

export const substanceConfigEntrySchema = z.object({
  memberProgramId: z.string(),
  programKey: z.string(),
  // Catalog display label (`program_definitions.display_name`) so clients can
  // render the program without a client-side key→label map (ADR-015).
  displayName: z.string(),
  domain: programDomainSchema,
  // `config` is `program_definitions.config` returned verbatim — a stored jsonb
  // blob whose keys are the program-spine data-model contract, not this
  // envelope's, so it keeps its own casing.
  // CLIENT parse shape, so the tolerant read schema: an installed build must keep
  // parsing a `config` blob from a server that has since gained a catalog field it
  // has never heard of. Strict here means the next additive field breaks every
  // runtime already in members' hands, and OTA cannot reach the older ones. The
  // producer schema below keeps the strict shape, so the server still cannot
  // silently emit a key nobody declared. Same client/producer split as
  // `checkInPoints` below.
  //
  // The tolerance is TOP-LEVEL ONLY. `ProgramConfigRead` is `ProgramConfig.strip()`
  // and `.strip()` does not recurse, so `care_track_rules`, `intake` and `taps2`
  // stay `.strict()` inside it: an additive field nested in one of those still
  // breaks installed builds exactly the way a top-level one would have. The tests
  // pin both halves of that boundary so the limit is recorded, not assumed. Making
  // the read shape deep-tolerant is tracked separately (see the nested-boundary test).
  config: ProgramConfigRead,
  // What one check-in against this member program WILL earn, resolved
  // server-side from the program's reward schedule (cap-clamped through the
  // same rule the write path mints with). `null` means this program runs no
  // check-in earn — a client sums the non-null values and shows nothing when
  // the total is zero, rather than deriving an amount of its own (ADR-015).
  //
  // Optional here because this is the CLIENT parse shape: a client build
  // carrying this schema must keep parsing a response from an API deployed
  // before the field existed. The producer schema below requires it, so a
  // server that stops computing the value fails its own response validation
  // instead of silently dropping the member's points promise.
  checkInPoints: z.number().int().nonnegative().nullable().optional(),
  // What attending a scheduled appointment under this member program WOULD
  // earn, resolved server-side from the same reward schedule. Prospective and
  // display-only — no write path mints against it yet. `null` means the
  // program's schedule carries no appointment earn. Optional for the same
  // old-API-compatibility reason as `checkInPoints` above.
  appointmentPoints: z.number().int().nonnegative().nullable().optional(),
  // The member programme's single sub-type tag (`member_program_tags.tag`), so a
  // client can pick a programme's per-type copy from the catalog without knowing
  // any sub-type itself (ADR-015). `null` when the programme carries no tag or
  // more than one — a member with two types gets the programme-level wording.
  //
  // Added to an existing client-parsed response: safe because this entry object
  // has been a non-strict `z.object` in every shipped bundle, so an installed
  // client strips the key it does not know (ADR-135 Rule 2). Optional here for the
  // other direction only — a client build reading an API deployed before it.
  subtypeTag: z.string().nullable().optional(),
});
export type SubstanceConfigEntry = z.infer<typeof substanceConfigEntrySchema>;

export const substanceConfigResponseSchema = apiEnvelope(z.array(substanceConfigEntrySchema));
export type SubstanceConfigResponse = z.infer<typeof substanceConfigResponseSchema>;

/**
 * What the SERVER validates its own response against, and what the generated
 * OpenAPI documents: `checkInPoints` is required (nullable, but present).
 *
 * The strictness runs opposite ways on purpose, exactly as the check-in write
 * response does. Sharing one optional schema across both directions would let a
 * regression that stops populating the field pass response validation and be
 * documented as conforming, while the member simply loses the points promise
 * with nothing failing.
 */
export const substanceConfigProducerEntrySchema = substanceConfigEntrySchema.extend({
  // Re-tightened for the producer: the client tolerates a catalog key it has not
  // shipped yet, but the server must not emit one nobody declared — an undeclared
  // key here means the catalog and this contract have drifted.
  config: ProgramConfig,
  checkInPoints: z.number().int().nonnegative().nullable(),
  appointmentPoints: z.number().int().nonnegative().nullable(),
  subtypeTag: z.string().nullable(),
});
export type SubstanceConfigProducerEntry = z.infer<typeof substanceConfigProducerEntrySchema>;

export const substanceConfigProducerResponseSchema = apiEnvelope(
  z.array(substanceConfigProducerEntrySchema),
);
